import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CheckCircle2, Ban, HelpCircle, ShieldOff, Clock, ArrowRightLeft } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatDate } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { BackofficeMerchantClaimDto, MerchantClaimStatus, Tone } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  SearchInput,
  Select,
  Modal,
  TextInput,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import ClaimEvidencePanel from '../components/ClaimEvidencePanel';

/**
 * A fila de reivindicações de perfil (card 252, KU-13c).
 *
 * Aprovar aqui entrega a página de um negócio real a uma pessoa; revogar tira
 * de quem já a administrava. Os dois têm alguém do lado de fora que descobre
 * pelo resultado, e é isso que explica as escolhas de tela abaixo.
 *
 * ## O motivo é obrigatório, e a tela não finge o contrário
 *
 * O servidor recusa decisão sem motivo. Em vez de deixar o operador descobrir
 * isso por um erro depois de clicar, o botão de confirmar nasce desabilitado.
 *
 * ## A ordem é do servidor
 *
 * Mais antigo primeiro, e não é opção de tela. A fila existe para que ninguém
 * espere demais; deixar ordenar por outra coisa é deixar o topo deixar de ser
 * quem mais esperou.
 */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Requester = styled.div`
  min-width: 0;

  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  small {
    display: block;
    color: ${(p) => p.theme.textMuted};
    font-size: 0.78rem;
  }
`;

/* O SLA em destaque quando passa de um dia. Não é enfeite: é o número que diz
   qual linha da fila já virou problema de atendimento. */
const Waiting = styled.span<{ $late: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.82rem;
  font-weight: ${(p) => (p.$late ? 700 : 500)};
  color: ${(p) => (p.$late ? p.theme.error : p.theme.textMuted)};
`;

/* O `Badge` não aceita `style` de propósito - é o componente que garante que
   todos os selos do backoffice tenham a mesma forma. O espaçamento fica num
   invólucro, que é onde ele pertence. */
const TransferTag = styled.span`
  margin-left: 0.4rem;
`;

const Detail = styled.div`
  display: grid;
  gap: 0.65rem;

  dl {
    display: grid;
    grid-template-columns: 150px 1fr;
    gap: 0.4rem 1rem;
    margin: 0;
    font-size: 0.88rem;
  }
  dt {
    color: ${(p) => p.theme.textMuted};
  }
  dd {
    margin: 0;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const Warning = styled.p`
  margin: 0;
  padding: 0.75rem 0.9rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.warning}1a;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textPrimary};
`;

const STATUS_LABELS: Record<MerchantClaimStatus, string> = {
  NONE: 'Sem pedido',
  IN_PROGRESS: 'Iniciado',
  PENDING_VALIDATION: 'Aguardando código',
  UNDER_REVIEW: 'Em análise',
  APPROVED: 'Aprovado',
  REJECTED: 'Recusado',
  NEEDS_INFO: 'Falta informação',
};

const CHANNEL_LABELS: Record<string, string> = {
  WHATSAPP: 'WhatsApp da empresa',
  EMAIL: 'E-mail da empresa',
  CORPORATE_EMAIL: 'E-mail corporativo',
  DOCUMENT: 'Documento (análise)',
};

const FILTER_OPTIONS = [
  { value: '', label: 'Todos os estados' },
  { value: 'UNDER_REVIEW', label: 'Em análise' },
  { value: 'NEEDS_INFO', label: 'Falta informação' },
  { value: 'PENDING_VALIDATION', label: 'Aguardando código' },
  { value: 'APPROVED', label: 'Aprovados' },
  { value: 'REJECTED', label: 'Recusados' },
];

/** Acima disto o pedido é destacado como atrasado. */
const SLA_HOURS = 24;

function statusTone(status: MerchantClaimStatus): Tone {
  if (status === 'APPROVED') return 'success';
  if (status === 'REJECTED') return 'danger';
  if (status === 'UNDER_REVIEW' || status === 'NEEDS_INFO') return 'warning';
  return 'secondary';
}

type DecisionKind = 'APPROVED' | 'REJECTED' | 'NEEDS_INFO' | 'REVOKE';

const DECISION_COPY: Record<DecisionKind, { title: string; confirm: string; hint: string }> = {
  APPROVED: {
    title: 'Aprovar reivindicação',
    confirm: 'Aprovar',
    hint: 'A pessoa passa a administrar a página desta empresa. Isto não cria parceria nem concede selo.',
  },
  REJECTED: {
    title: 'Recusar reivindicação',
    confirm: 'Recusar',
    hint: 'O motivo é mostrado a quem pediu. Escreva algo que explique a decisão para ela, não só para nós.',
  },
  NEEDS_INFO: {
    title: 'Pedir mais informação',
    confirm: 'Pedir informação',
    hint: 'O pedido continua na fila. Diga exatamente o que falta - "documento insuficiente" faz a pessoa adivinhar.',
  },
  REVOKE: {
    title: 'Revogar acesso',
    confirm: 'Revogar',
    hint: 'A empresa fica sem responsável imediatamente, e quem administrava perde o acesso agora.',
  },
};

interface Props {
  /** Permissões efetivas de quem está na tela. */
  permissions: string[];
  isAdmin: boolean;
}

const ClaimsPage: React.FC<Props> = ({ permissions, isAdmin }) => {
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(
    () => backofficeApi.listMerchantClaims({ size: 100 }),
    [],
  );

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('UNDER_REVIEW');
  const [detail, setDetail] = useState<BackofficeMerchantClaimDto | null>(null);
  const [deciding, setDeciding] = useState<{ claim: BackofficeMerchantClaimDto; kind: DecisionKind } | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const claims = useMemo(() => data?.content ?? [], [data]);

  const stats = useMemo(
    () => ({
      total: data?.totalElements ?? claims.length,
      review: claims.filter((c) => c.status === 'UNDER_REVIEW' || c.status === 'NEEDS_INFO').length,
      late: claims.filter(
        (c) => (c.status === 'UNDER_REVIEW' || c.status === 'NEEDS_INFO') && c.waitingHours >= SLA_HOURS,
      ).length,
      transfers: claims.filter((c) => c.transfer && c.status !== 'APPROVED' && c.status !== 'REJECTED').length,
    }),
    [data, claims],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return claims.filter((c) => {
      const matchesStatus = !statusFilter || c.status === statusFilter;
      const matchesTerm =
        !term ||
        [c.merchantName, c.requesterName, c.requesterEmail, c.merchantCity ?? ''].some((v) =>
          v.toLowerCase().includes(term),
        );
      return matchesStatus && matchesTerm;
    });
  }, [claims, search, statusFilter]);

  const openDecision = (claim: BackofficeMerchantClaimDto, kind: DecisionKind): void => {
    setDeciding({ claim, kind });
    setReason('');
  };

  const confirmDecision = async (): Promise<void> => {
    if (!deciding) return;
    const { claim, kind } = deciding;
    setSubmitting(true);
    try {
      if (kind === 'REVOKE') {
        await backofficeApi.revokeMerchantClaim(claim.id, { reason: reason.trim() });
        showNotification('Acesso revogado.', 'success');
      } else {
        await backofficeApi.decideMerchantClaim(claim.id, { decision: kind, reason: reason.trim() });
        showNotification('Decisão registrada.', 'success');
      }
      setDeciding(null);
      setDetail(null);
      await reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível registrar a decisão.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Reivindicações"
        subtitle="Pedidos de responsáveis para assumir a página da própria empresa"
      />

      <StatsGrid>
        <StatCard label="Pedidos" value={String(stats.total)} />
        <StatCard label="Esperando decisão" value={String(stats.review)} />
        <StatCard label={`Esperando +${SLA_HOURS}h`} value={String(stats.late)} />
        <StatCard label="Transferências" value={String(stats.transfers)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por empresa, solicitante ou e-mail..."
        />
        <Select
          options={FILTER_OPTIONS}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </Toolbar>

      <DataState loading={loading} error={error} onRetry={reload} isEmpty={filtered.length === 0}>
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Solicitante</th>
                <th>Canal</th>
                <th>Estado</th>
                <th>Esperando</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((claim) => (
                <tr key={claim.id}>
                  <td>
                    <Requester>
                      <strong>{claim.merchantName}</strong>
                      <small>{claim.merchantCity || '-'}</small>
                    </Requester>
                  </td>
                  <td>
                    <Requester>
                      <strong>{claim.requesterName}</strong>
                      <small>{claim.requesterRole}</small>
                    </Requester>
                  </td>
                  <td>
                    {CHANNEL_LABELS[claim.channel] || claim.channel}
                    {claim.transfer && (
                      <TransferTag>
                        <Badge tone="warning">
                          <ArrowRightLeft size={11} /> Transferência
                        </Badge>
                      </TransferTag>
                    )}
                  </td>
                  <td>
                    <Badge tone={statusTone(claim.status)}>{STATUS_LABELS[claim.status]}</Badge>
                  </td>
                  <td>
                    <Waiting $late={claim.waitingHours >= SLA_HOURS && claim.status !== 'APPROVED'}>
                      <Clock size={13} /> {claim.waitingHours}h
                    </Waiting>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionButton title="Ver detalhe" onClick={() => setDetail(claim)}>
                      <HelpCircle size={16} />
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {/* Detalhe: tudo o que a pessoa declarou, mais o que a validação disse.
          O contato da empresa aparece mascarado - quem precisa dele abre o
          cadastro da empresa, que é onde ele legitimamente está. */}
      <Modal isOpen={!!detail} onClose={() => setDetail(null)} title="Pedido de reivindicação" width="640px">
        {detail && (
          <Detail>
            {detail.transfer && (
              <Warning>
                <strong>É uma transferência.</strong> Esta empresa já tem responsável ativo. Aprovar
                tira o acesso de quem administra hoje - confirme com as duas partes antes.
              </Warning>
            )}
            <dl>
              <dt>Empresa</dt>
              <dd>{detail.merchantName}{detail.merchantCity ? ` · ${detail.merchantCity}` : ''}</dd>
              <dt>Solicitante</dt>
              <dd>{detail.requesterName} - {detail.requesterRole}</dd>
              <dt>E-mail</dt>
              <dd>{detail.requesterEmail}</dd>
              <dt>Telefone</dt>
              <dd>{detail.requesterPhone || '-'}</dd>
              <dt>Documento</dt>
              <dd>{detail.requesterDocument || '-'}</dd>
              <dt>Observação</dt>
              <dd>{detail.note || '-'}</dd>
              <dt>Canal</dt>
              <dd>{CHANNEL_LABELS[detail.channel] || detail.channel}</dd>
              <dt>Destino do código</dt>
              <dd>{detail.destination || '-'}</dd>
              <dt>Validado em</dt>
              <dd>{detail.validatedAt ? formatDate(detail.validatedAt) : 'não validado por código'}</dd>
              <dt>Tentativas / reenvios</dt>
              <dd>{detail.codeAttempts} / {detail.codeResends}</dd>
              <dt>Aberto em</dt>
              <dd>{formatDate(detail.createdAt)} · esperando {detail.waitingHours}h</dd>
              {detail.decisionReason && (
                <>
                  <dt>Decisão</dt>
                  <dd>{detail.decisionReason}</dd>
                </>
              )}
            </dl>

            {/* Card 277 - os anexos ficam aqui, no detalhe, porque é onde a
                decisão sobre o pedido é tomada. Só o metadado é carregado; o
                documento em si só é buscado por clique, e o clique fica
                registrado. */}
            <ClaimEvidencePanel claimId={detail.id} permissions={permissions} isAdmin={isAdmin} />

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {detail.status === 'APPROVED' ? (
                <Button variant="outline" onClick={() => openDecision(detail, 'REVOKE')}>
                  <ShieldOff size={16} /> Revogar acesso
                </Button>
              ) : (
                <>
                  <Button variant="primary" onClick={() => openDecision(detail, 'APPROVED')}>
                    <CheckCircle2 size={16} /> Aprovar
                  </Button>
                  <Button variant="outline" onClick={() => openDecision(detail, 'NEEDS_INFO')}>
                    <HelpCircle size={16} /> Pedir informação
                  </Button>
                  <Button variant="outline" onClick={() => openDecision(detail, 'REJECTED')}>
                    <Ban size={16} /> Recusar
                  </Button>
                </>
              )}
            </div>
          </Detail>
        )}
      </Modal>

      <Modal
        isOpen={!!deciding}
        onClose={() => setDeciding(null)}
        title={deciding ? DECISION_COPY[deciding.kind].title : ''}
        width="520px"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeciding(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              // O servidor recusa sem motivo. Desabilitar aqui evita que o
              // operador descubra isso por um erro depois de clicar.
              disabled={reason.trim().length === 0 || submitting}
              isLoading={submitting}
              onClick={confirmDecision}
            >
              {deciding ? DECISION_COPY[deciding.kind].confirm : ''}
            </Button>
          </>
        }
      >
        {deciding && (
          <Detail>
            <p style={{ margin: 0, fontSize: '0.88rem' }}>{DECISION_COPY[deciding.kind].hint}</p>
            <TextInput
              label="Motivo"
              placeholder="Fica no histórico e é mostrado a quem pediu"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Detail>
        )}
      </Modal>
    </>
  );
};

export default ClaimsPage;
